// Hybrid retrieval: BM25 + dense + graph-RAG with cross-encoder rerank,
// semantic dedup and multi-hop citation following. Fully simulated but
// deterministic enough to render meaningfully in the UI.

import { DEFAULT_CONFIG, type ResearchConfig } from "./config";
import type { RetrievedDoc, ToolHint } from "./types";
import { pick, rand, sleep, uid } from "./util";

// Lightweight realistic corpus seeded around AI/alignment topics.
// Mixed origin (papers, blogs, reports) to look authentic in the UI.
const CORPUS: Array<Omit<RetrievedDoc, "score" | "layer">> = [
  { id: "d_1",  title: "Constitutional AI: Harmlessness from AI Feedback",
    source: "arxiv.org/abs/2212.08073", snippet: "RLAIF + constitution → reduced harmful outputs without supervised labels.",
    publishedAt: "2022-12-15", authors: ["Bai et al."] },
  { id: "d_2",  title: "Weak-to-strong generalization",
    source: "openai.com/research/weak-to-strong-generalization",
    snippet: "Weaker supervisors elicit latent capabilities of stronger student models.",
    publishedAt: "2023-12-14", authors: ["Burns et al."] },
  { id: "d_3",  title: "Sleeper Agents: Training Deceptive LLMs",
    source: "arxiv.org/abs/2401.05566",
    snippet: "Backdoored models persist through RLHF + adversarial training.",
    publishedAt: "2024-01-12", authors: ["Hubinger et al."] },
  { id: "d_4",  title: "Scalable Oversight via Debate",
    source: "arxiv.org/abs/1805.00899",
    snippet: "Two AIs argue, a human judges; convergence under amplification.",
    publishedAt: "2018-05-02", authors: ["Irving et al."] },
  { id: "d_5",  title: "Mechanistic Interpretability of Sparse Autoencoders",
    source: "transformer-circuits.pub/2024/scaling-monosemanticity",
    snippet: "SAEs surface monosemantic features in production-scale LLMs.",
    publishedAt: "2024-05-21", authors: ["Anthropic Interp Team"] },
  { id: "d_6",  title: "RLHF and the False Promise of Reward Models",
    source: "arxiv.org/abs/2310.01405",
    snippet: "Reward hacking emerges as policy capability scales; KL bounds insufficient.",
    publishedAt: "2023-10-02", authors: ["Casper et al."] },
  { id: "d_7",  title: "Direct Preference Optimization",
    source: "arxiv.org/abs/2305.18290",
    snippet: "DPO removes RL loop; matches PPO on alignment benchmarks.",
    publishedAt: "2023-05-29", authors: ["Rafailov et al."] },
  { id: "d_8",  title: "Process Supervision Outperforms Outcome Supervision",
    source: "openai.com/research/improving-mathematical-reasoning",
    snippet: "Step-level reward models reduce reasoning errors in math.",
    publishedAt: "2023-05-31", authors: ["Lightman et al."] },
  { id: "d_9",  title: "Eliciting Latent Knowledge",
    source: "ai-alignment.com/elk",
    snippet: "ELK problem: ensure reporters do not strategically lie about world model.",
    publishedAt: "2021-12-18", authors: ["Christiano et al."] },
  { id: "d_10", title: "Inverse Constitutional AI",
    source: "arxiv.org/abs/2406.05881",
    snippet: "Recover principles from preference datasets via interpretable extraction.",
    publishedAt: "2024-06-09", authors: ["Findeis et al."] },
  { id: "d_11", title: "Anthropic Responsible Scaling Policy v2",
    source: "anthropic.com/news/announcing-our-updated-responsible-scaling-policy",
    snippet: "Capability thresholds gating deployment; commitment to evaluations.",
    publishedAt: "2024-10-15", authors: ["Anthropic Policy"] },
  { id: "d_12", title: "Deliberative Alignment",
    source: "openai.com/research/deliberative-alignment",
    snippet: "Models reason explicitly over safety policies before answering.",
    publishedAt: "2024-12-22", authors: ["Guan et al."] },
  { id: "d_13", title: "Steering LLMs via activation patching",
    source: "transformer-circuits.pub/2023/activation-patching",
    snippet: "Localized circuit edits change behavior without retraining.",
    publishedAt: "2023-08-04", authors: ["Olsson et al."] },
  { id: "d_14", title: "Gradient-based adversarial suffixes (GCG)",
    source: "arxiv.org/abs/2307.15043",
    snippet: "Universal jailbreak suffixes transfer across aligned LLMs.",
    publishedAt: "2023-07-27", authors: ["Zou et al."] },
  { id: "d_15", title: "Goal misgeneralization",
    source: "arxiv.org/abs/2210.01790",
    snippet: "Capable agents pursue mis-specified goals far OOD.",
    publishedAt: "2022-10-04", authors: ["Langosco et al."] },
  { id: "d_16", title: "Open Problems in Cooperative AI",
    source: "arxiv.org/abs/2012.08630",
    snippet: "Coordination among AIs as a key alignment desideratum.",
    publishedAt: "2020-12-15", authors: ["Dafoe et al."] },
  { id: "d_17", title: "Self-Consistency Improves Chain-of-Thought",
    source: "arxiv.org/abs/2203.11171",
    snippet: "Marginalize over sampled reasoning paths; majority vote on answer.",
    publishedAt: "2022-03-21", authors: ["Wang et al."] },
  { id: "d_18", title: "AI 2027 — Forecasting the next 24 months",
    source: "ai-2027.org/forecast",
    snippet: "Scenario planning for frontier capability deployment.",
    publishedAt: "2025-02-18", authors: ["Multi-author forecast"] },
  { id: "d_19", title: "RLAIF beats RLHF on helpfulness benchmarks",
    source: "arxiv.org/abs/2309.00267",
    snippet: "AI-generated feedback approaches human-quality preference signal.",
    publishedAt: "2023-09-01", authors: ["Lee et al."] },
  { id: "d_20", title: "UK AISI Evaluations Framework",
    source: "aisi.gov.uk/evaluations",
    snippet: "Public-sector capability evaluations for cyber, bio, autonomy.",
    publishedAt: "2024-11-04", authors: ["UK AI Safety Institute"] },
  { id: "d_21", title: "Tree of Thoughts deliberation",
    source: "arxiv.org/abs/2305.10601",
    snippet: "Search-based reasoning improves planning at inference.",
    publishedAt: "2023-05-17", authors: ["Yao et al."] },
  { id: "d_22", title: "Interpretability + control: Steering vectors",
    source: "transformer-circuits.pub/2024/steering-vectors",
    snippet: "Activation steering changes refusal behavior without retraining.",
    publishedAt: "2024-04-10", authors: ["Templeton et al."] },
  { id: "d_23", title: "Emergent deception in instruct-tuned models",
    source: "arxiv.org/abs/2402.10260",
    snippet: "Models exhibit goal-conditioned deceptive behavior under pressure.",
    publishedAt: "2024-02-15", authors: ["Scheurer et al."] },
  { id: "d_24", title: "Alignment as a Multi-Agent Problem",
    source: "deepmind.google/alignment-multi-agent",
    snippet: "Game-theoretic framing: incentives over single objectives.",
    publishedAt: "2024-08-22", authors: ["DeepMind Safety"] },
];

// Citation graph (sparse, plausible) for multi-hop demos.
const CITES: Record<string, string[]> = {
  d_1:  ["d_19", "d_10"],
  d_2:  ["d_5", "d_22"],
  d_3:  ["d_15", "d_23"],
  d_4:  ["d_16", "d_24"],
  d_5:  ["d_22", "d_13"],
  d_6:  ["d_7", "d_8"],
  d_7:  ["d_19"],
  d_8:  ["d_21"],
  d_9:  ["d_5"],
  d_10: ["d_1"],
  d_11: ["d_20"],
  d_12: ["d_8", "d_21"],
  d_13: ["d_22"],
  d_14: ["d_3", "d_15"],
  d_15: ["d_23"],
  d_17: ["d_21"],
  d_18: ["d_11"],
  d_22: ["d_5"],
  d_23: ["d_3"],
  d_24: ["d_16"],
};

const TOKEN_RE = /[a-z0-9]+/gi;
function tokens(s: string) {
  const m = s.toLowerCase().match(TOKEN_RE);
  return m ?? [];
}
function bm25Score(query: string, doc: string) {
  const q = tokens(query);
  const d = tokens(doc);
  if (!q.length || !d.length) return 0;
  const set = new Set(d);
  let hits = 0;
  for (const t of q) if (set.has(t)) hits++;
  return hits / Math.sqrt(q.length * d.length);
}
function denseScore(query: string, doc: string) {
  // cheap pseudo-embedding: hashed bigram overlap + length penalty
  const qg = bigrams(query.toLowerCase());
  const dg = bigrams(doc.toLowerCase());
  if (!qg.size || !dg.size) return 0;
  let inter = 0;
  for (const g of qg) if (dg.has(g)) inter++;
  return inter / Math.sqrt(qg.size * dg.size);
}
function bigrams(s: string) {
  const out = new Set<string>();
  const t = s.replace(/\s+/g, " ");
  for (let i = 0; i < t.length - 1; i++) out.add(t.slice(i, i + 2));
  return out;
}

export interface RetrieveArgs {
  query: string;
  signal?: AbortSignal;
  config?: ResearchConfig;
  onLayer?: (layer: "bm25" | "dense" | "graph" | "rerank", latencyMs: number, count: number) => Promise<void> | void;
  onTool?: (tool: ToolHint, latencyMs: number) => Promise<void> | void;
}

export interface RetrieveResult {
  docs: RetrievedDoc[];
  layers: Record<"bm25" | "dense" | "graph" | "rerank", number>;
  hops: Array<{ from: string; to: string }>;
  totalLatencyMs: number;
}

export async function hybridRetrieve(args: RetrieveArgs): Promise<RetrieveResult> {
  const cfg = args.config ?? DEFAULT_CONFIG;
  const startedAt = performance.now();

  // BM25
  const bm25Lat = rand(cfg.retrieval.layerLatencyMs.bm25.min, cfg.retrieval.layerLatencyMs.bm25.max);
  await sleep(bm25Lat, args.signal);
  const bm25Hits: RetrievedDoc[] = CORPUS
    .map((d) => ({ ...d, layer: "bm25" as const, score: bm25Score(args.query, `${d.title} ${d.snippet}`) }))
    .filter((d) => d.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, cfg.retrieval.bm25TopK);
  await args.onLayer?.("bm25", bm25Lat, bm25Hits.length);
  await args.onTool?.("bm25", bm25Lat);

  // Dense
  const denseLat = rand(cfg.retrieval.layerLatencyMs.dense.min, cfg.retrieval.layerLatencyMs.dense.max);
  await sleep(denseLat, args.signal);
  const denseHits: RetrievedDoc[] = CORPUS
    .map((d) => ({ ...d, layer: "dense" as const, score: denseScore(args.query, `${d.title} ${d.snippet}`) }))
    .filter((d) => d.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, cfg.retrieval.denseTopK);
  await args.onLayer?.("dense", denseLat, denseHits.length);
  await args.onTool?.("dense", denseLat);

  // Graph-RAG: expand neighbors of top-k seeds via citations
  const graphLat = rand(cfg.retrieval.layerLatencyMs.graph.min, cfg.retrieval.layerLatencyMs.graph.max);
  await sleep(graphLat, args.signal);
  const seeds = [...bm25Hits.slice(0, 4), ...denseHits.slice(0, 4)];
  const graphSet = new Map<string, RetrievedDoc>();
  for (const s of seeds) {
    const neigh = CITES[s.id] ?? [];
    for (const nid of neigh) {
      const base = CORPUS.find((d) => d.id === nid);
      if (base) graphSet.set(nid, { ...base, layer: "graph", score: 0.5 + Math.random() * 0.4 });
    }
  }
  const graphHits = Array.from(graphSet.values()).slice(0, cfg.retrieval.graphTopK);
  await args.onLayer?.("graph", graphLat, graphHits.length);
  await args.onTool?.("graph_rag", graphLat);

  // Multi-hop: walk citation chain up to depth N from top-2 dense seeds
  const hops: Array<{ from: string; to: string }> = [];
  const hopVisited = new Set<string>();
  const enqueue: Array<{ id: string; chain: string[] }> = denseHits.slice(0, 2).map((d) => ({ id: d.id, chain: [d.id] }));
  while (enqueue.length) {
    const cur = enqueue.shift()!;
    if (cur.chain.length > cfg.retrieval.multiHopDepth) continue;
    const next = CITES[cur.id] ?? [];
    for (const nid of next) {
      hops.push({ from: cur.id, to: nid });
      if (!hopVisited.has(nid)) {
        hopVisited.add(nid);
        enqueue.push({ id: nid, chain: [...cur.chain, nid] });
      }
    }
  }
  await args.onTool?.("multi_hop", 60);

  // Merge + dedup by id, keeping max score per doc
  const merged = new Map<string, RetrievedDoc>();
  for (const d of [...bm25Hits, ...denseHits, ...graphHits]) {
    const ex = merged.get(d.id);
    if (!ex || d.score > ex.score) merged.set(d.id, { ...d, hopChain: [d.id] });
  }
  // Attach hop chains for graph-discovered docs
  for (const h of hops) {
    const target = merged.get(h.to);
    if (target) target.hopChain = Array.from(new Set([h.from, h.to, ...(target.hopChain ?? [])]));
  }

  // Rerank (cross-encoder simulated)
  const rerankLat = rand(cfg.retrieval.layerLatencyMs.rerank.min, cfg.retrieval.layerLatencyMs.rerank.max);
  await sleep(rerankLat, args.signal);
  const reranked = Array.from(merged.values())
    .map((d) => ({
      ...d,
      layer: "rerank" as const,
      score: 0.45 * d.score + 0.55 * (denseScore(args.query, `${d.title} ${d.snippet}`) + 0.05),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, cfg.retrieval.rerankTopK);
  await args.onLayer?.("rerank", rerankLat, reranked.length);
  await args.onTool?.("rerank", rerankLat);

  return {
    docs: reranked,
    layers: { bm25: bm25Hits.length, dense: denseHits.length, graph: graphHits.length, rerank: reranked.length },
    hops,
    totalLatencyMs: performance.now() - startedAt,
  };
}

export function pickEvidenceForQuestion(question: string, docs: RetrievedDoc[], n = 3): RetrievedDoc[] {
  return docs.slice(0, n).map((d) => ({ ...d, score: d.score * (0.85 + Math.random() * 0.3) }));
}

export function spinClaimText(question: string, doc: RetrievedDoc): string {
  const verbs = ["suggests", "indicates", "shows", "argues", "demonstrates", "reports"];
  return `${doc.authors?.[0] ?? "The source"} ${pick(verbs)} that ${doc.snippet.replace(/\.$/, "")}, addressing: "${question.slice(0, 80)}".`;
}
